<template>
  <div>
    <Header
      ref="header"
      :title="$route.meta.title"
      :buttons="[]"
    >
      <v-btn
        class="px-4"
        color="primary"
        outlined
        v-shortkey="exportShortkeys"
        @shortkey.native="exportReport"
        @click="exportReport"
      >
        {{ getLabelWithShortkey('Eksportuj do csv', exportShortkeys.key) }}
      </v-btn>
    </Header>
    <Table
      ref="table"
      :table-name="tableName"
      disable-pagination
      disable-personalization
      hide-statistics-bar
      @openDetails="openDriverDetails"
    >
      <template #tableTop>
        <div ref="tableTop">
          <FiltersBar
            :table-name="tableName"
            search-bar
          >
            <template #filters-top-right>
              <div class="d-flex align-center">
                <v-autocomplete
                  v-model="vehicleType"
                  class="driver-settlements__select ml-4"
                  :items="vehicleTypeOptions"
                  label="Typ pojazdu"
                  placeholder="Wybierz typ pojazdu"
                  outlined
                  hide-details
                  append-icon="mdi-chevron-down"
                  dense
                  clearable
                  @change="setVehicleType"
                />
                <MonthPicker v-model="month" />
                <v-btn
                  class="px-4 ml-2"
                  color="primary"
                  outlined
                  v-shortkey="toggleStatsShortkeys"
                  @shortkey.native="toggleStats"
                  @click="toggleStats"
                >
                  {{ getLabelWithShortkey(showStats ? 'Ukryj statystyki' : 'Pokaż statystyki', toggleStatsShortkeys.key) }}
                </v-btn>
              </div>
            </template>
          </FiltersBar>
          <DriverSettlementStats
            v-if="showStats"
            :items="stats"
          />
        </div>
      </template>
    </Table>
  </div>
</template>

<script>
import DateTime from 'luxon/src/datetime'
import { mapActions, mapState } from 'vuex'
import Header from '../../components/Layout/Header'
import Table from '../../components/Table/Table'
import FiltersBar from '../../components/Filters/FiltersBar'
import MonthPicker from '../../components/Filters/MonthPicker'
import DriverSettlementStats from '../../components/Reports/DriverSettlementStats'
import exportRowsToCsv from '../../utils/exportRowsToCsv'
import {
  exportShortkeys,
  toggleStatsShortkeys,
  getLabelWithShortkey
} from '../../const/shortKeys'

const SUMMARY_HEADERS = [
  { text: 'Kierowca', value: 'driver' },
  { text: 'Średnia długość pętli', value: 'averageLoopLength' },
  { text: 'Średni czas pętli', value: 'averageLoopTime' },
  { text: 'Średni czas pracy', value: 'averageWorkTime' },
  { text: 'Średnia ilość pętli', value: 'averageLoops' },
  { text: 'Pętla bezwzględna', value: 'absoluteLoops' },
  { text: 'Typ pojazdu', value: 'vehicleType' }
]

const STAT_CARDS = [
  { key: 'averageLoops', icon: 'loop', label: 'Średnia ilość pętli', iconWidth: 48, iconHeight: 48 },
  { key: 'averageLoopLength', icon: 'map-marker-distance', label: 'Średnia długość pętli', composite: true, iconWidth: 64, iconHeight: 64 },
  { key: 'containerRotation', icon: 'rotate-360', label: 'Rotacja kontenera', iconWidth: 48, iconHeight: 48 },
  { key: 'averageLoopTime', icon: 'clock', label: 'Średni czas pętli', composite: true, iconWidth: 64, iconHeight: 64 },
  { key: 'driversCount', icon: 'account-multiple', label: 'Kierowców', hint: 'aktywnych', composite: true, iconWidth: 64, iconHeight: 64 },
  { key: 'averageWorkTime', icon: 'clock', label: 'Średni czas pracy', hint: 'dziennie', composite: true, iconWidth: 64, iconHeight: 64 },
  { key: 'absoluteAverageLoops', icon: 'loop', label: 'Średnia ilość pętli', hint: 'bezwzględna', iconWidth: 48, iconHeight: 48 },
  { key: 'containersCount', icon: 'dump-truck', label: 'Liczba kontenerów', composite: true, iconWidth: 64, iconHeight: 64 }
]

export default {
  components: {
    Header,
    Table,
    FiltersBar,
    MonthPicker,
    DriverSettlementStats
  },
  data: () => ({
    tableName: 'driversSummary',
    month: DateTime.local().toFormat('yyyy-MM'),
    vehicleType: null,
    showStats: true,
    exportShortkeys,
    toggleStatsShortkeys
  }),
  computed: {
    ...mapState({
      items: state => state.driversSummary.items,
      summary: state => state.driversSummary.summary,
      departmentId: state => state.core.department?.id,
      vehicleTypeOptions: state => state.core.filters.vehicleTypes || []
    }),
    stats () {
      return STAT_CARDS.map(card => ({
        ...card,
        value: this.summary?.[card.key] ?? '-'
      }))
    }
  },
  watch: {
    month (value) {
      this.setTableDate({ date: [`${value}-01`], fetch: true })
    },
    departmentId () {
      this.getItems()
    },
    showStats () {
      this.$nextTick(() => {
        if (this.$refs.table) this.$refs.table.resizeTable()
      })
    }
  },
  created () {
    this.setTableDate({ date: [`${this.month}-01`], fetch: false })
  },
  methods: {
    ...mapActions({
      setTableDate: 'driversSummary/setTableDate',
      setVehicleType: 'driversSummary/setVehicleType',
      getItems: 'driversSummary/getItems'
    }),
    getLabelWithShortkey,
    toggleStats () {
      this.showStats = !this.showStats
    },
    openDriverDetails (item) {
      this.$router.push({
        name: 'driversDetails',
        query: { driverId: String(item.id), month: this.month }
      })
    },
    exportReport () {
      exportRowsToCsv(
        SUMMARY_HEADERS,
        this.items,
        `zestawienie-kierowcow-${this.month}.csv`,
        this.stats
      )
    }
  }
}
</script>
